import Content from "./content";
import FirmReviewEdit from "~/review/edit";
import FirmReviewPopup from "~/review/popup";
import FirmStarsReview from "~/review/stars-review";
import { node, icon } from "~/util/dom";
import { dateFormat } from "~/util/format";
import { firmStats, getBadgeComposedComponent, getFirmLayout, reviewGetBadgeColor, reviewHasBadge, reviewHasColoredBadge, reviewHasCountBadge } from "~/util/review";
import { showAlternativesPOI } from "~/review/alternate";
import { store } from "~/state/store";
import { setReviewPopupState } from "~/state/review/state";
import { IGetDetailOutput, getDetail } from "~/util/providers/review";
import { IReviewConf } from "~/interfaces";
import { IReview, IReviewItem, IStarsInterval } from "~/review/interfaces";
import { _ } from "~/i18n";
import { CONFIG } from "~/config";
import { FULLTEXT_REVIEW } from "~/review/const";
import Notifications, { NOTIFICATIONS_TYPE } from "~/util/notifications";
import ReviewPopup from "~/review/popupwc";

// Upravit hodnoceni | Pridat hodnoceni | Zobrazit více
function createButton(txt: string, cb: (event: MouseEvent) => void, noStar?: boolean) {
	const btnEl = node("button");

	btnEl.classList.add("firm-btn");
	btnEl.setAttribute("data-log-ignore", "true");
	btnEl.addEventListener("click", event => {
		cb(event);
	});

	if (!noStar) {
		btnEl.appendChild(icon("star"));
	}

	btnEl.appendChild(node("span", { textContent: txt || "" }));

	return btnEl;
}

function scrollToComments() {
	const commentsEl = document.querySelector("#detail .content-comments") as HTMLElement;

	commentsEl?.scrollIntoView();
}

// signal: firmreview-review-myreview
export default class Review extends Content<IReviewConf> {
	_logSource: string;

	_firmReviewEdit: FirmReviewEdit;

	constructor(data: IReviewConf, options) {
		super(data, options);

		this._logSource = "recenze-poi";
		this._firmReviewEdit = null;

		// vlastni build
		window.addEventListener("review-report", this);
		window.addEventListener("firmreview-comments-myreview", this);

		this._set();

		const { user } = store.getState().user;

		// pokud chceme hned hodnotit, ale uzivatel neni prihlaseny, tak ho donutime se prihlasit na nove strance, protoze popup byva blokovany
		if (this._data.immediatelyOpen && user.state !== "green") {
			// pokud to jde, ziskame si spolehlive URL, pokud ne, tak si ji vymyslime
			if ("login" in window) {
				window.location.href = window.login.link().href;
			} else {
				const params = new URLSearchParams();

				params.set("return_url", (window as Window & typeof globalThis).location.href);
				params.set("serviceId", "mapy");

				(window as Window & typeof globalThis).location.href = `https://login.szn.${CONFIG.TLD}?${params.toString()}`;
			}
		}

		// analytika z url
		if (this._data.openData?.service === FULLTEXT_REVIEW) {
			this._logSource = FULLTEXT_REVIEW;
		}

		if ((this._data.openReviewPopup?.stars === 0 || (this._data.openReviewPopup?.stars > 0 && this._data.myReview)) || (this._data.immediatelyOpen && this._data.openReviewPopup)) {
			this._openReviewPopup(JSON.parse(JSON.stringify(this._data.openReviewPopup)));
		} else if (this._data.immediatelyOpen) {
			this._openReviewPopup();
		}

		if (this._data.openReviewPopup) {
			delete this._data.openReviewPopup;
		}
	}

	destroy() {
		super.destroy();
		this._firmReviewEdit && this._closeEdit();
		window.removeEventListener("review-report", this);
		window.removeEventListener("firmreview-comments-myreview", this);
	}

	handleEvent(event: CustomEvent) {
		switch (event.type) {
			case "review-report": {
				this._data.review.total--;

				this._set();
				break;
			}

			case "firmreview-comments-myreview": {
				const detail: {
					rusAdmin: boolean;
					review: IReview;
					myReview: IReviewItem;
				} = event.detail;

				this._data.rusAdmin = detail.rusAdmin;
				this._data.review = detail.review || this._data.review;
				this._data.myReview = detail.myReview;

				this._set();
				break;
			}

			default:
		}
	}

	_build() {
		this._dom.container.classList.add("content-review");
	}

	_set() {
		this._dom.container.innerHTML = "";
		this._dom.container.classList.remove("partners-review-open");
		this._dom.container.classList.remove("empty");
		// uzivatel je vlastnik
		this._dom.container.classList[this._data.review && this._data.rusAdmin ? "add" : "remove"]("firm-owner");

		let isEmpty = true;
		const isBooking = this._data.booking && this._data.booking.review_score;

		// firmy
		if (this._data.review) {
			const firmLine = node("div", { className: "firm-line" });

			if (!this._data.myReview && !reviewHasColoredBadge(this._data.review) && !this._data.review.use_total && !this._data.inactive && !this._data.rusAdmin) {
				if (this._data.isFirm) {
					const starsReview = new FirmStarsReview({
						data: JSON.parse(JSON.stringify(this._data)),
						logSource: this._logSource,
						logPosition: "top",
						onLoginError: data => {
							if (data.isOwner) {
								this._data.rusAdmin = true;
								this._dom.container.classList[this._data.review && this._data.rusAdmin ? "add" : "remove"]("firm-owner");
								starsReview.destroy();
								Notifications.notify({
									content: _("detail.content.review2.errorOwner"),
									type: NOTIFICATIONS_TYPE.ERROR,
								});
								this._callEvent();
							} else if (data.myReview) {
								this._data.myReview = data.myReview;
								starsReview.destroy();
								this._set();
								this._openEdit();
								this._callEvent();
							}
						},
						showPopupCb: putReviewData => {
							// ulozime
							if (putReviewData.review) {
								this._data.review = putReviewData.review;
							}

							this._data.myReview = putReviewData.myReview;
							// popup
							if (Mapy.CUS.review?.webcomponent) {
								this._createReviewPopup(null, putReviewData.myReview?.rating || undefined);
							} else {
								this._createReviewPopup();
							}
						},
						onStructuredRating: data => {
							this._data.id = data.id;
							this._data.source = data.source;
							this._data.review = data.review;

							this._createReviewPopup(null, data.rating);
						},
						onError: () => {
							this._log("reviews_fail_stars");
						},
					});

					firmLine.appendChild(starsReview.container);
				} else {
					const container = node("div", { className: "add-review-other" });

					container.append(node("h3", { className: "title", innerHTML: _("detail.content.review2.addOther") }));
					container.append(this._createAddReviewButton());
					firmLine.appendChild(container);
				}

				isEmpty = false;
			} else {
				this._dom.reviewLine = node("div", { className: "review-line" });

				if (reviewHasColoredBadge(this._data.review) || (reviewHasCountBadge(this._data.review) && !isBooking)) {
					const reviewValue = node("a", { className: "stars", href: "#" });

					reviewValue.addEventListener("click", event => {
						event.preventDefault();
						// analytika
						this._log("click", {
							text: "goToReviews",
							badge: reviewHasColoredBadge(this._data.review) ? 1 : 0,
						});

						// skocit na recenze
						scrollToComments();
					});
					this._dom.reviewLine.appendChild(reviewValue);

					if (reviewHasBadge(this._data.review)) {
						reviewValue.appendChild(node(getBadgeComposedComponent(), {
							type: reviewGetBadgeColor(this._data.review),
							size: "medium",
							rating: this._data.review.review_rating_stars,
							text: this._data.review.review_rating_caption,
							reviews: this._data.review.total,
							lang: CONFIG.defaultLang,
						}));
					}

					isEmpty = false;
				}

				firmLine.appendChild(this._dom.reviewLine);
				// tlacitko pro pridani hodnoceni
				if (!this._data.myReview && !this._data.inactive && !this._data.rusAdmin) {
					this._dom.reviewLine.appendChild(this._createAddReviewButton());

					isEmpty = false;
				}

				// sipky pro zobrazeni hodnoceni
				if (isBooking && reviewHasColoredBadge(this._data.review)) {
					const partnersReview = node("div", { className: "partners-review" });

					partnersReview.appendChild(node("span", { className: "desc", textContent: _("detail.content.review2.partnersReview") }));
					partnersReview.appendChild(icon("dropdown"));
					partnersReview.appendChild(icon("dropup"));
					partnersReview.addEventListener("click", () => {
						// analytika
						this._log("click", {
							text: this._dom.container.classList.contains("partners-review-open") ? "partnersRating-hide" : "partnersRating-show",
						});
						this._dom.container.classList.toggle("partners-review-open");
					});
					this._dom.container.classList.add("has-partners-review");
					this._dom.reviewLine.appendChild(partnersReview);
					isEmpty = false;
				}
			}

			if (!isEmpty) {
				this._dom.container.appendChild(firmLine);
			}
		}

		// booking
		if (isBooking) {
			const bookingLine = node("div", { className: "booking-line" });
			const booking = node("span", { className: "booking-items" });

			booking.appendChild(node("img", { src: "/img/booking.svg", className: "booking-logo" }));

			const totalReview = node("span", { className: "total-review" });

			totalReview.appendChild(node("strong", { textContent: Mapy.Util.formatNumber(this._data.booking.review_score || 0, 1, false, true) }));
			totalReview.appendChild(document.createTextNode(" /10"));
			booking.appendChild(totalReview);

			const reviewLink = node("a", { href: `${this._data.booking.url}#tab-reviews`, target: "_blank", rel: "nofollow", className: "total-count", textContent: `(${this._data.booking.num_reviews})` });

			reviewLink.addEventListener("click", () => {
				const bookingLog = {
					id: this._data.id,
					source: this._data.source,
					name: this._data.title,
					typeId: 3,
					placeId: 1,
					target: "booking",
				};

				Mapy.getComponent("logger").log("clickBooking", bookingLog);
				// pouze pro ceske veci
				if (this._data.isFirm) {
					firmStats("firmy-vizitka-akce", this._data, {
						placeId: 1,
						typeId: 28,
					});
				}
			});
			bookingLine.appendChild(booking);
			bookingLine.appendChild(reviewLink);
			this._dom.bookingLine = bookingLine;
			this._dom.container.appendChild(bookingLine);
			isEmpty = false;
		}

		// vlastni hodnoceni
		if (this._data.review && this._data.myReview) {
			// zobrazime info o hodnoceni
			const updateReview = this._data.myReview.actual === false && !this._data.inactive;
			const myreviewLine = node("div", { className: "myreview-line" });
			const myreviewInfo = node("div", { className: "myreview-info" });
			let reviewInfoTextContent = "";
			let isRejected = false;

			if (this._data.myReview?.state === "rejected") {
				isRejected = true;
				myreviewLine.classList.add("rejected");
				reviewInfoTextContent = _("detail.content.review2.rejected");
			} else {
				reviewInfoTextContent = _(
					updateReview ? "detail.content.review2.reviewRefreshDate" : "detail.content.review2.reviewDate",
					{ date: dateFormat(new Date(this._data.myReview.updated), "d. m. yyyy") },
				);
			}

			const reviewInfo = node("p", { textContent: reviewInfoTextContent, ...isRejected ? { className: "rejected" } : {} });

			myreviewInfo.appendChild(reviewInfo);
			// firemni hodnoceni
			this._firmReviewEdit = new FirmReviewEdit({
				data: this._data,
				logSource: this._logSource,
				thankYouCont: this._dom.container,
				onSave: putReviewData => {
					this._closeEdit();

					// ulozime
					if (putReviewData.review) {
						this._data.review = putReviewData.review;
					}

					this._data.myReview = putReviewData.myReview;
					// nastavime nova data
					this._set();
					this._callEvent();
				},
				onCancel: () => {
					this._closeEdit();
				},
			});

			if (!this._data.inactive) {
				if (isRejected) {
					myreviewInfo.appendChild(createButton(
						_("detail.content.review2.more"),
						() => {
							// analytika
							this._log("click", {
								text: "goToRefusedReview",
								section: "reviews",
							});

							scrollToComments();
						},
						true,
					));
				} else {
					myreviewInfo.appendChild(createButton(_(updateReview ? "detail.content.review2.refreshReview" : "detail.content.review2.editReview"), () => {
						// analytika
						this._log("click", {
							text: updateReview ? "Aktualizovat hodnocení" : "Upravit hodnocení",
							type: updateReview ? "recenze-update" : "recenze-edit",
						});
						this._openEdit();
					}));
				}
			}

			if (updateReview) {
				myreviewLine.classList.add("review-actual");
			}

			myreviewLine.appendChild(myreviewInfo);
			this._dom.container.appendChild(myreviewLine);
			this._dom.myreviewLine = myreviewLine;
			this._dom.myreviewInfo = myreviewInfo;
			this._dom.reviewInfo = reviewInfo;
			isEmpty = false;
		}

		// neni booking nebo hodnoceni
		if (isEmpty) {
			this._dom.container.classList.add("empty");
		}
	}

	_openEdit() {
		if (!Mapy.CUS.review?.webcomponent) {
			this._dom.myreviewLine.appendChild(this._firmReviewEdit.container);
			this._dom.myreviewLine.classList.add("edit-review");
		}

		this._firmReviewEdit.openEdit();
		store.dispatch(setReviewPopupState({
			edit: true,
		}));
	}

	_closeEdit() {
		if (!Mapy.CUS.review?.webcomponent) {
			this._firmReviewEdit.container.remove();
			this._dom.myreviewLine.classList.remove("edit-review");
		}

		store.dispatch(setReviewPopupState({
			edit: false,
		}));
	}

	_createAddReviewButton() {
		return createButton(_("detail.content.review2.addReview"), () => {
			// analytika
			this._log("click", {
				text: "Přidat hodnocení",
			});

			this._openReviewPopup();
		});
	}

	_openReviewPopup(openReviewPopup?: IStarsInterval) {
		if (this._data.rusAdmin) {
			Notifications.notify({
				content: _("detail.content.review2.errorOwner"),
				type: NOTIFICATIONS_TYPE.ERROR,
			});

			return;
		}

		const { user } = store.getState().user;

		(user.state === "green"
			? Promise.resolve()
			: Mapy.getComponent("login").open({
				analyticsData: {
					loginOrigin: "reviewsAdd",
					section: "reviews",
				},
				motivation: "review",
			})
		).then(async () => {
			const isAlternatives = this._data.review?.alternatives?.length;
			const forceOpen = openReviewPopup?.stars === 0 || this._data.myReview;

			if (Mapy.CUS.review?.webcomponent) {
				// pridani hodnoceni
				const data = await (user.state === "green" ? Promise.resolve(this._data) : getDetail({ source: this._data.source, id: this._data.id }));

				if ((data as IGetDetailOutput).isOwner) {
					this._data.rusAdmin = true;
					this._dom.container.classList[this._data.review && this._data.rusAdmin ? "add" : "remove"]("firm-owner");
					Notifications.notify({
						content: _("detail.content.review2.errorOwner"),
						type: NOTIFICATIONS_TYPE.ERROR,
					});
					this._callEvent();

					return;
				} else if (data.myReview) {
					this._data.myReview = data.myReview;
					this._set();
					this._openEdit();
					this._callEvent();

					return;
				}

				// zadavani noveho hodnoceni
				this._createReviewPopup(openReviewPopup || (isAlternatives && !openReviewPopup), openReviewPopup?.percent || 0);
			} else if (!isAlternatives || (isAlternatives && (forceOpen || (!openReviewPopup && await showAlternativesPOI(this._data, this._data.review?.alternatives, null, {
				source: this._logSource,
			}))))) {
				// pridani hodnoceni
				(user.state === "green" ? Promise.resolve(this._data) : getDetail({ source: this._data.source, id: this._data.id })).then(data => {
					if (data.isOwner) {
						this._data.rusAdmin = true;
						this._dom.container.classList[this._data.review && this._data.rusAdmin ? "add" : "remove"]("firm-owner");
						Notifications.notify({
							content: _("detail.content.review2.errorOwner"),
							type: NOTIFICATIONS_TYPE.ERROR,
						});
						this._callEvent();

						return;
					} else if (data.myReview) {
						this._data.myReview = data.myReview;
						this._set();
						this._openEdit();
						this._callEvent();

						if (openReviewPopup?.stars === 0) {
							setTimeout(() => {
								this._firmReviewEdit.container.scrollIntoView();
							}, 200);
						}

						return;
					}

					// popup
					this._createReviewPopup(openReviewPopup || (isAlternatives && !openReviewPopup), openReviewPopup?.percent || 0);
				});
			}
		}, () => { /**/ });
	}

	// eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
	_createReviewPopup(openReviewPopup?, forceRating = 0) {
		// vlozeni recenze
		store.dispatch(setReviewPopupState({
			create: true,
		}));

		if (Mapy.CUS.review?.webcomponent) {
			// eslint-disable-next-line no-new
			new ReviewPopup({
				data: this._data,
				textReview: !!this._data.myReview,
				logSource: this._logSource,
				logPosition: "top",
				forceRating,
				fromOtherPOI: !!openReviewPopup,
				onClose: closeData => {
					if (closeData?.review) {
						this._data.review = closeData.review;
					}

					this._data.myReview = closeData.myReview;

					// upravime se sami
					this._set();
					this._callEvent();

					store.dispatch(setReviewPopupState({
						create: false,
					}));
				},
			});
		} else {
			// eslint-disable-next-line no-new
			new FirmReviewPopup({
				data: this._data,
				textReview: !!this._data.myReview,
				logSource: this._logSource,
				logPosition: "top",
				forceRating,
				fromOtherPOI: !!openReviewPopup,
				onClose: closeData => {
					if (closeData.review) {
						this._data.review = closeData.review;
					}

					this._data.myReview = closeData.myReview;
					// upravime se sami
					this._set();
					this._callEvent();

					store.dispatch(setReviewPopupState({
						create: false,
					}));
				},
			});
		}
	}

	_callEvent() {
		// zavolame callback pro comments
		window.dispatchEvent(new CustomEvent("firmreview-review-myreview", {
			detail: {
				rusAdmin: this._data.rusAdmin,
				review: this._data.review,
				myReview: this._data.myReview,
			},
		}));

		// data mezi taby
		const { data: detailData } = store.getState().detail;

		Mapy.getComponent("detail").getBroadCast()?.postMessage(JSON.stringify({
			type: "firm-review",
			data: {
				rusAdmin: this._data.rusAdmin,
				review: this._data.review,
				myReview: this._data.myReview,
				source: detailData?.source,
				id: detailData?.id,
			},
		}));
	}

	_log(name: string, data?: object) {
		// analytika
		/* eslint-disable camelcase */
		const reviewObj: {
			[key: string]: string | number | boolean;
			refused?: 0 | 1;
		} = {
			position: "top",
			source: this._logSource,
			section: "reviews",
			rating_layout: getFirmLayout(this._data.review, this._data.booking, this._data.myReview, this._data.isFirm),
			sourcePoi: this._data.source,
			typeid: this._data.typeId,
			firmTwin: this._data.isTwin ? 1 : 0,
			type: "recenze",
			...data,
		};

		if (reviewObj.type === "recenze-edit") {
			reviewObj.refused = this._data.myReview?.state === "rejected" ? 1 : 0;
		}

		Mapy.getComponent("logger").log(name, reviewObj);
	}
}
